import type { CaseDetail } from '@/shared/domain/case';
import type { MriDimension, MriFieldAdmin } from '@/shared/domain/mriAdmin';
import type { Characteristic, Offering } from '@/shared/domain/serviceCatalog';

/* How MRI is assembled: the field set pinned to a case decides which fields
  apply, and the extracted values fill them in. Nothing here stores a gap count
  — it is always derived, so a count can never drift from the fields it claims
  to describe.

   `MriValue` and `MriRow` are defined here rather than in `serviceCatalog.ts`
   or `case.ts`, because they belong to neither bounded context on their own —
  they are MRI's own read model, built from a field and a case's
   extracted values, and keeping them beside the functions that assemble them
   avoids a cross-context dependency neither context otherwise needs. */

/** One extracted value, with the source it came from and who put it there. */
export interface MriValue {
  readonly group: MriDimension;
  readonly name: string;
  readonly value: string;
  readonly source: string;
  readonly confidence: number;
  readonly author: 'AI' | 'Human';
}

/** One MRI field occurrence as the reviewer sees it: filled or still open. */
export interface MriRow {
  readonly name: string;
  readonly businessName: string;
  readonly dimension: MriDimension;
  readonly value: string | null;
  readonly source: string | null;
  readonly confidence: number | null;
  readonly author: 'AI' | 'Human' | null;
}

export function findOffering(offerings: readonly Offering[], id: string): Offering | undefined {
  return offerings.find((offering) => offering.id === id);
}

/** A case carries the offering name, so the binding is resolved by name. */
export function offeringByName(
  offerings: readonly Offering[],
  name: string,
): Offering | undefined {
  return offerings.find((offering) => offering.name === name);
}

export function mandatoryMriCount(fields: readonly MriFieldAdmin[]): number {
  return fields.filter((field) => field.minCardinality >= 1).length;
}

/** Free-text annotation fields the extraction pipeline is allowed to record more
    than once per case (`services/api` calls these its repeatable-merge fields).
    Each occurrence is a genuine, distinct note or comment worth keeping — that is
    why the backend never deduplicates them — but a reviewer reads "Notes" as one
    section of a case, not as a row per email that mentioned something. Merging
    only these three names, rather than every repeated field, matters: a field
    repeated outside this set (an approver, a WBS element) is not several notes
    agreeing with each other, it is a conflict, and conflict rows stay separate so
    a reviewer can see and resolve the disagreement. */
const MERGED_TEXT_FIELDS: ReadonlySet<string> = new Set(['comments', 'notes', 'supportingdocuments']);

/** One row standing in for every occurrence of a merged free-text field. The
    value is every occurrence's text, in extraction order; the source is every
    distinct source that contributed one. Confidence takes the lowest of the set
    rather than an average, because a reviewer weighing whether to trust the
    combined text should be shown its weakest link, not a number smoothed by its
    strongest. Authorship favours `Human` if any occurrence carries it: one
    corrected line among several extracted ones makes the field human-touched. */
function mergedTextRow(field: MriFieldAdmin, occurrences: readonly MriValue[]): MriRow {
  const sources = [...new Set(occurrences.map((value) => value.source))];
  return {
    name: field.name,
    businessName: field.businessName ?? field.name,
    dimension: field.mriDimension,
    value: occurrences.map((value) => value.value).join('; '),
    source: sources.join('; ') || null,
    confidence: Math.min(...occurrences.map((value) => value.confidence)),
    author: occurrences.some((value) => value.author === 'Human') ? 'Human' : 'AI',
  };
}

/** MRI as the reviewer sees it: every mandatory field plus populated optional fields. */
export function caseMri(
  fields: readonly MriFieldAdmin[],
  values: readonly MriValue[],
  missing: readonly string[],
): readonly MriRow[] {
  const byName = new Map<string, MriValue[]>();
  for (const value of values) {
    const occurrences = byName.get(value.name) ?? [];
    occurrences.push(value);
    byName.set(value.name, occurrences);
  }

  return fields.flatMap<MriRow>((field) => {
    const found = missing.includes(field.name) ? [] : (byName.get(field.name) ?? []);
    if (found.length === 0) {
      if (field.minCardinality < 1) return [];
      return [{
        name: field.name,
        businessName: field.businessName ?? field.name,
        dimension: field.mriDimension,
        value: null,
        source: null,
        confidence: null,
        author: null,
      }];
    }
    if (found.length > 1 && MERGED_TEXT_FIELDS.has(field.name.toLowerCase())) {
      return [mergedTextRow(field, found)];
    }
    return found.map((value) => ({
      name: field.name,
      businessName: field.businessName ?? field.name,
      dimension: field.mriDimension,
      value: value.value,
      source: value.source,
      confidence: value.confidence,
      author: value.author,
    }));
  });
}

/** How the case reached us, in one phrase. */
export function originOf(detail: CaseDetail): string {
  if (detail.channels.length > 1) return `ServiceNow ${detail.ticket ?? '—'} + email thread`;
  if (detail.channels[0] === 'SNOW') return `ServiceNow ${detail.ticket ?? '—'} only`;
  return 'Email only — no ticket raised';
}

/** Characteristics an operator would name in conversation, matched loosely so
    "waiting on a WBS" finds wbsElement. */
export function findCharacteristic(
  characteristics: readonly Characteristic[],
  text: string,
): Characteristic | undefined {
  const haystack = text.toLowerCase();
  return characteristics.find(
    (characteristic) =>
      haystack.includes(characteristic.businessName.toLowerCase()) ||
      haystack.includes(characteristic.name.toLowerCase()),
  );
}
